import map from 'lodash/map';
import omit from 'lodash/omit';
import { v4 as uuid } from 'uuid';
import Cookies from 'js-cookie';

import type { AnalyticsDefaultPayload, AnalyticsSuppliedPayload } from './common';

// const TIMEOUT = 10 * 1000;

/**
 * Get the current values from `window`
 */
const getPageParams = () =>
  typeof window !== 'undefined'
    ? {
        url: window.document.URL,
        title: window.document.title,
        referrer: window.document.referrer,
        userAgent: window.navigator.userAgent,
        language: window.navigator.language,
      }
    : {};

const DEFAULT_OPTS: AnalyticsDefaultPayload = {
  clientType: 'browser',
  releaseEnv: 'dev',
  ...getPageParams(),
};
let defaultOpts: AnalyticsDefaultPayload | null = null;
let analyticsURL = '';

let userID: string | null = null;
let engagementID: string | null = null;

// Queue to hold events that come in before initialization.
// They will be flushed after initialization.
const eventQueue: AnalyticsSuppliedPayload[] = [];

// sessionStorage polyfill for Android in-app WebView which may have no sessionStorage
let _sessionSorageData = {};
const sessionStorage: Storage =
  (typeof window !== 'undefined' && window.sessionStorage) ||
  ({
    setItem: (key: string, value: string): void => {
      _sessionSorageData[key] = value;
    },
    getItem: (key: string): string | undefined => {
      return _sessionSorageData[key];
    },
    removeItem: (key: string): boolean => {
      return delete _sessionSorageData[key];
    },
    clear: (): void => {
      _sessionSorageData = {};
    },
  } as unknown as Storage);

/**
 * Initialize payload
 *
 * @param url - Analytics server endpoint
 * @param opts
 * @param pageEvent - Sends the `page` event before flushing the event queue.
 */
export const initialize = async (
  url: string,
  opts: Partial<AnalyticsDefaultPayload & { engagementID: string }>,
  pageEvent = true
): Promise<void> => {
  defaultOpts = { ...(defaultOpts || DEFAULT_OPTS), ...omit(opts, ['engagementID', 'metadata']) };
  analyticsURL = url;
  engagementID = opts.engagementID ?? '';

  if (pageEvent) {
    await page(opts.metadata);
  }

  // Flush event queue
  await Promise.all(map(eventQueue, (event) => send(event)));
};

// URL for the previous `page` event. Used to avoid sending duplicate track events for same page.
let prevURL: string | undefined = undefined;

/**
 * This is the function that does the actual sending of event to backend.
 * @param payload - Payload to send
 */
const send = async (payload: AnalyticsSuppliedPayload): Promise<void> => {
  if (!defaultOpts) {
    console.warn('Jerry Analytics:', 'Analytics event called BEFORE initialization. Skipping.', payload);
    return;
  }

  const body: AnalyticsSuppliedPayload & AnalyticsDefaultPayload = {
    ...defaultOpts,
    ...getPageParams(),
    ...payload,
    eventMetadata: { ...payload.eventMetadata, engagementID },
    userID,
  };

  // Don't send `page` event if it is the same as the previous URL.
  if (body.eventCategory === 'page') {
    if (prevURL === body.url) return;
    prevURL = body.url;
  }

  if (body.eventCategory === 'page' || body.eventCategory === 'identify') {
    const firstUrlFromCookie = Cookies.get('firstUrl');
    if (body.url && !firstUrlFromCookie && body.releaseEnv !== 'dev') {
      Cookies.set('firstUrl', body.url);
      Cookies.set('firstReferer', body.referrer || '');
    }
  }

  if (body.releaseEnv === 'dev') {
    console.groupCollapsed('Jerry Analytics');
    console.info(JSON.stringify(body, null, 2));
    console.groupEnd();
    return;
  }

  if (!analyticsURL && body.releaseEnv !== 'dev') {
    console.warn('Jerry Analytics:', 'No analytics URL provided. Skipping.');
    return;
  }

  void fetch(analyticsURL, {
    method: 'POST',
    body: JSON.stringify(body),
    // Use keepalive {@url https://developer.mozilla.org/en-US/docs/Web/API/RequestInit#keepalive}
    //   to make sure event tracking request is sent to server
    keepalive: true,
    headers: {
      'Content-Type': 'text/plain',
    },
  });
};

/**
 * Track event
 *
 * @param payload
 * @param noErrors - If `true`, catches and suppresses any errors.
 *                   In most cases this value should be set to `true`.
 */
export const trackEvent = async (payload: AnalyticsSuppliedPayload, noErrors = true): Promise<void> => {
  // If not initialized, push into queue.
  if (!defaultOpts) {
    if (!noErrors) {
      throw new Error('Cannot wait on errors from deferred events.');
    }
    eventQueue.push(payload);
    return;
  }

  // maintain the event chain by have previousEventId in eventMetadata
  // use sessionStorage so that it would be automatically cleared after the session end
  const storageIdKey = 'jerry/track/lastTrackEventId';
  const eventID = uuid();
  const store = sessionStorage;
  const previousEventID = store.getItem(storageIdKey) ?? undefined;
  store.setItem(storageIdKey, eventID);
  payload.eventID = eventID;
  payload.eventMetadata = {
    ...payload.eventMetadata,
    previousEventID,
  };

  try {
    const res = await send(payload);
    return res;
  } catch (err) {
    if (!noErrors) {
      throw err;
    }
  }
};

/**
 * Track
 *
 * @param event
 * @param metadata
 */
export const track = async (
  event: { category: string; action?: string; label?: string },
  metadata: { [key: string]: string | number } | null = null,
  noErrors = true
): Promise<void> =>
  trackEvent(
    {
      eventCategory: event.category,
      eventAction: event.action || '',
      eventLabel: event.label,
      eventMetadata: metadata,
    },
    noErrors
  );

/**
 * Page
 *
 * @param metadata
 * @param noErrors
 */
export const page = async (
  metadata: { [key: string]: string | number } | null = null,
  noErrors = true
): Promise<void> =>
  trackEvent(
    {
      eventCategory: 'page',
      eventAction: '',
      eventMetadata: metadata,
    },
    noErrors
  );

/**
 * pageFullyLoaded
 *
 * @param metadata
 * @param noErrors
 */
export const pageFullyloaded = async (
  metadata: { [key: string]: string | number } | null = null,
  noErrors = true
): Promise<void> =>
  trackEvent(
    {
      eventCategory: 'pageFullyLoaded',
      eventAction: '',
      eventMetadata: metadata,
    },
    noErrors
  );

/**
 * Identify
 *
 * @param userID
 */
export const identify = async (id: string | null, noErrors = true): Promise<void> => {
  userID = id;
  return trackEvent(
    {
      eventCategory: 'identify',
      eventAction: '',
    },
    noErrors
  );
};

/**
 * Engagement id changed
 */
export const onEngagementIdChange = (id: string, engagementEvent = true): void => {
  const prevEngagementID = engagementID;
  engagementID = id ?? '';
  if (engagementEvent) {
    void trackEvent({
      eventCategory: 'engagement',
      eventAction: '',
      eventMetadata: { prevEngagementID },
    });
  }
};
